// src/pages/admin/BranchForm.jsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import API from "../../api";
import AddressFields from "../../components/common/address/AddressFields";
import {
    hasAnyAddress,
    validateAddressIfPresent,
} from "../../components/common/address/addressValidators";
import PageHeaderSection from "../../components/layout/PageHeaderSection";
import PageLoadingShell from "../../components/layout/PageLoadingShell";
import Button from "../../components/common/Button";
import PhoneNumberFields from "../../components/common/PhoneNumberFields";
import { validateStoredPhone } from "../../utils/phone";
import { getApiErrorInfo, isUpgradeRequiredError } from "../../utils/apiError";
import {
    getBranchModeOptions,
    branchShowsAddress,
} from "../../config/branchModes";
import { TextInput, SelectInput, CheckboxField } from "../../components/common/form";

const BranchForm = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);

    const [form, setForm] = useState({
        name: "",
        branchCode: "",
        phoneNumber: "",
        mode: "OFFLINE", // ONLINE | OFFLINE
        active: true,
        // address fields (hidden when ONLINE)
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        country: "",
        pincode: "",
        latitude: "",
        longitude: "",
        // NEW: optional manager assignment
        managedByUserId: "",
    });

    const [loading, setLoading] = useState(isEdit);
    const [loadError, setLoadError] = useState("");
    const [submitError, setSubmitError] = useState(null);

    // Managers dropdown state
    const [managers, setManagers] = useState([]);
    const [managersLoading, setManagersLoading] = useState(false);
    const [managersErr, setManagersErr] = useState(null);

    // Load MANAGER users for dropdown
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setManagersLoading(true);
                // Keep path style consistent with other calls in this file ("/admin/..."):
                const res = await API.get("/admin/users", {
                    params: { role: "MANAGER", active: true, page: 0, size: 200 },
                });
                if (!mounted) return;
                const data = res?.data;
                const items = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.content)
                        ? data.content
                        : [];
                setManagers(items);
                setManagersErr(null);
            } catch (e) {
                if (!mounted) return;
                setManagersErr(t('forms.branch.loadManagersFailed'));
            } finally {
                if (!mounted) return;
                setManagersLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    // Load existing branch when editing
    useEffect(() => {
        if (!isEdit) return;
        const load = async () => {
            setLoading(true);
            setLoadError("");
            try {
                const res = await API.get(`/admin/branches/${id}`);
                const b = res.data || {};
                const a = b.address || {};
                setForm({
                    name: b.name || "",
                    branchCode: b.branchCode || "",
                    phoneNumber: b.phoneNumber || "",
                    mode: b.mode || "OFFLINE",
                    active: b.active !== false,
                    addressLine1: a.addressLine1 || "",
                    addressLine2: a.addressLine2 || "",
                    city: a.city || "",
                    state: a.state || "",
                    country: a.country || "",
                    pincode: a.pincode || "",
                    latitude: a.latitude != null ? String(a.latitude) : "",
                    longitude: a.longitude != null ? String(a.longitude) : "",
                    managedByUserId: b.managerId || "",
                });
            } catch {
                setLoadError(t('forms.branch.loadFailed', { defaultValue: 'Could not load this branch.' }));
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id, isEdit, t]);

    const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (submitError) setSubmitError(null);
        setField(name, type === "checkbox" ? checked : value);
    };

    const handleAddressChange = (partial) => {
        if (submitError) setSubmitError(null);
        setForm((prev) => ({ ...prev, ...partial }));
    };

    const validate = () => {
        if (!form.name.trim()) {
            alert(t('forms.branch.nameRequired'));
            return false;
        }
        if (!form.branchCode.trim()) {
            alert(t('forms.branch.codeRequired'));
            return false;
        }
        if (!form.mode) {
            alert(t('forms.branch.modeRequired'));
            return false;
        }

        // Address validation only when OFFLINE (address section visible)
        if (form.mode !== "ONLINE") {
            const addr = {
                addressLine1: form.addressLine1,
                addressLine2: form.addressLine2,
                city: form.city,
                state: form.state,
                country: form.country,
                pincode: form.pincode,
                latitude: form.latitude,
                longitude: form.longitude,
            };
            if (hasAnyAddress(addr)) {
                const v = validateAddressIfPresent(addr);
                if (!v.ok) {
                    alert(v.message);
                    return false;
                }
            }
        }

        // Manager selection is OPTIONAL → no validation required

        if (form.phoneNumber) {
            const phoneCheck = validateStoredPhone(form.phoneNumber);
            if (!phoneCheck.valid) {
                alert(phoneCheck.message);
                return false;
            }
        }

        return true;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const {
            name,
            branchCode,
            phoneNumber,
            mode,
            active,
            addressLine1,
            addressLine2,
            city,
            state,
            country,
            pincode,
            latitude,
            longitude,
            managedByUserId,
        } = form;

        const includeAddress =
            branchShowsAddress(mode) &&
            hasAnyAddress({
                addressLine1,
                addressLine2,
                city,
                state,
                country,
                pincode,
                latitude,
                longitude,
            });

        const payload = {
            name: name.trim(),
            branchCode: branchCode.trim(),
            phoneNumber: phoneNumber?.trim()
                ? validateStoredPhone(phoneNumber).normalized
                : "",
            mode,
            active,
            // NEW: send optional manager assignment (null if none)
            managedByUserId:
                managedByUserId !== "" && managedByUserId != null
                    ? Number(managedByUserId)
                    : null,
            ...(includeAddress && {
                address: {
                    addressLine1,
                    addressLine2,
                    city,
                    state,
                    country,
                    pincode,
                    latitude: latitude !== "" && latitude != null ? parseFloat(latitude) : null,
                    longitude: longitude !== "" && longitude != null ? parseFloat(longitude) : null,
                },
            }),
        };

        try {
            setSubmitError(null);
            if (isEdit) {
                await API.put(`/admin/branches/${id}`, payload);
                alert(t('forms.branch.updateSuccess'));
            } else {
                await API.post("/admin/branches", payload);
                alert(t('forms.branch.addSuccess'));
            }
            navigate("/admin/branches");
        } catch (err) {
            console.error("Save failed", err);
            const errorInfo = getApiErrorInfo(
                err,
                isEdit ? t('forms.branch.updateFailed') : t('forms.branch.saveFailed'),
            );

            const uiError = {
                ...errorInfo,
                variant: isUpgradeRequiredError(err) ? "warning" : "error",
            };

            setSubmitError(uiError);
            alert(`${uiError.title}\n\n${uiError.message}`);
        }
    };

    if (loading) {
        return (
            <PageLoadingShell
                variant="page-shell-form"
                title={isEdit ? t('forms.branch.editTitle') : t('forms.branch.addTitle')}
                subtitle={t('forms.branch.subtitle')}
            />
        );
    }

    if (isEdit && loadError) {
        return (
            <div className="page-shell-form">
                <PageHeaderSection
                    title={t('forms.branch.editTitle')}
                    subtitle={t('forms.branch.subtitle')}
                />
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                    {loadError}
                </div>
                <div className="mt-4">
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/branches')}>
                        {t('common.back', { defaultValue: 'Back' })}
                    </Button>
                </div>
            </div>
        );
    }

    const Label = ({ children, required }) => (
        <label className="form-label">
            {children} {required && <span className="text-red-500">*</span>}
        </label>
    );

    const showAddress = form.mode !== "ONLINE";

    // Helper to render manager option label consistently
    const renderManagerLabel = (u) => {
        const id = u.userId ?? u.id;
        const name = [u.firstName, u.middleName, u.lastName]
            .filter(Boolean)
            .join(" ")
            .trim();

        return name ? `${name} ${id ? `(#${id})` : ""}` : (id ? `#${id}` : "—");

    };

    return (
        <div className="page-shell-form">
            <PageHeaderSection
                title={isEdit ? t('forms.branch.editTitle') : t('forms.branch.addTitle')}
                subtitle={t('forms.branch.subtitle')}
            />

            {submitError && (
                <div
                    className={
                        submitError.variant === "warning"
                            ? "banner-warning"
                            : "banner-danger"
                    }
                >
                    <p className="font-semibold">{submitError.title}</p>
                    <p className="mt-1 text-sm">{submitError.message}</p>
                </div>
            )}

            <form onSubmit={onSubmit} className="form-shell">
                <div className="form-section grid grid-cols-1 gap-3 md:grid-cols-2">
                    <TextInput
                        label={t('forms.branch.branchName')}
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder={t('forms.branch.namePlaceholder')}
                        required
                    />

                    <TextInput
                        label={t('forms.branch.branchCode')}
                        type="text"
                        name="branchCode"
                        value={form.branchCode}
                        onChange={handleChange}
                        placeholder={t('forms.branch.uniqueCodePlaceholder')}
                        required
                    />
                </div>

                <div className="form-section grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div>
                        <Label>{t('forms.common.phoneNumber')}</Label>
                        <PhoneNumberFields
                            value={form.phoneNumber}
                            onChange={(v) => setForm((prev) => ({ ...prev, phoneNumber: v }))}
                        />
                    </div>

                    <div className="md:col-span-2">
                        <Label required>{t('forms.branch.branchMode')}</Label>
                        <div className="mt-2 grid gap-2 sm:grid-cols-3">
                            {getBranchModeOptions().map((opt) => (
                                <label
                                    key={opt.value}
                                    className={`flex cursor-pointer gap-3 rounded-xl border p-3 transition ${
                                        form.mode === opt.value
                                            ? 'border-primary-400 bg-primary-50 ring-2 ring-primary-100'
                                            : 'border-slate-200 bg-white hover:border-primary-200'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="mode"
                                        value={opt.value}
                                        checked={form.mode === opt.value}
                                        onChange={handleChange}
                                        className="mt-1 shrink-0"
                                        required
                                    />
                                    <span>
                                        <span className="block text-sm font-semibold text-slate-900">
                                            {opt.label}
                                        </span>
                                        <span className="mt-0.5 block text-xs text-slate-500">
                                            {opt.description}
                                        </span>
                                    </span>
                                </label>
                            ))}
                        </div>
                        {form.mode === "ONLINE" && (
                            <p className="field-note mt-2">
                                {t('forms.branch.onlineNote')}
                            </p>
                        )}
                    </div>
                </div>

                {/* NEW: Manager (optional) */}
                <div className="form-section">
                    <SelectInput
                        label={t('forms.branch.managerOptional')}
                        name="managedByUserId"
                        value={form.managedByUserId ?? ""}
                        onChange={handleChange}
                        disabled={managersLoading}
                        error={managersErr || undefined}
                    >
                        <option value="">{t('forms.common.none')}</option>
                        {managers.map((u) => {
                            const id = u.userId ?? u.id;
                            return (
                                <option key={id} value={id}>
                                    {renderManagerLabel(u)}
                                </option>
                            );
                        })}
                    </SelectInput>
                </div>

                <CheckboxField
                    layout="toggle-card"
                    label={t('forms.branch.branchActive')}
                    description={t('forms.branch.branchActiveDesc')}
                    name="active"
                    checked={form.active}
                    onChange={handleChange}
                />

                {showAddress && (
                    <AddressFields
                        value={{
                            addressLine1: form.addressLine1,
                            addressLine2: form.addressLine2,
                            city: form.city,
                            state: form.state,
                            country: form.country,
                            pincode: form.pincode,
                            latitude: form.latitude,
                            longitude: form.longitude,
                        }}
                        onChange={handleAddressChange}
                        title={t('forms.branch.branchAddress')}
                    />
                )}

                <div className="page-actions justify-end">
                    <Button
                        type="submit"
                        variant="gradient-primary"
                    >
                        {isEdit ? t('forms.branch.updateButton') : t('forms.branch.createButton')}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default BranchForm;
